import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import {
    Drawer, DrawerOverlay, DrawerContent, DrawerBody, Box, Stack, Grid, GridItem,
    Flex, Button, Divider, Icon,
} from "@chakra-ui/react";
import { MdVisibility } from "react-icons/md";
import ContentEditor from "./ContentEditor";
import OldContentPreview from "./OldContentPreview";
import ConfirmActivationDialog from "./ConfirmActivationDialog";
import DetailContentSections from "./DetailContentSections";
import useVersionHistory from "./useVersionHistory";
import { useWith2FA } from "../../tools/twoFactor";
import {
    FormHeader, EditWarningBanner,
    TypeVersionFields, LanguageRoleFields, PlatformDateFields, ChangeTypeFields
} from "./FormFieldsSections";

/**
 * Builds the initial form state from an existing document or default values.
 * In edit mode, version is cleared to allow auto-increment by the API.
 * @param {object|null} doc - Existing document for edit, or null for creation
 * @returns {object} Form state object
 */
function buildInitialState(doc) {
    return {
        type: doc?.type || "",
        version: doc ? "" : "1.0",
        language: doc?.language || "fr",
        target_role: doc?.target_role || "all",
        platforms: doc?.platforms?.join(", ") || "all",
        effective_date: "",
        change_type: "material",
        notify_users: true,
        content: doc?.content || { sections: [] }
    };
}

/**
 * Builds the API payload from form state. Omits version if empty (auto-increment).
 * change_type / notify_users are only sent on update (strict create schema).
 * @param {object} form - Form state object
 * @param {boolean} isEdit - Whether the document is being edited
 * @returns {object} Payload ready for API submission
 */
function buildPayload(form, isEdit) {
    const platforms = form.platforms.split(",").map(p => p.trim()).filter(Boolean);
    const payload = {
        type: form.type,
        language: form.language,
        target_role: form.target_role,
        platforms,
        content: form.content,
        effective_date: form.effective_date ? `${form.effective_date}T00:00:00` : null
    };
    if (form.version) payload.version = form.version;
    if (isEdit) {
        payload.change_type = form.change_type;
        payload.notify_users = form.notify_users;
    }
    return payload;
}

/**
 * Sends the form payload to the API for creation or update (2FA-protected).
 * @param {object} form - Current form state
 * @param {boolean} isEdit - Whether the document is being edited
 * @param {object|null} doc - Existing document (used for ID on update)
 * @param {function} setLoading - Loading state setter
 * @param {function} onSuccess - Callback with saved document on success
 * @param {function} runWith2FA - The 2FA-aware request runner
 */
async function submitFormToApi(form, isEdit, doc, setLoading, onSuccess, runWith2FA) {
    setLoading(true);
    const payload = buildPayload(form, isEdit);
    try {
        const url = isEdit ? `/admin/legal/update/${doc.id}` : "/admin/legal/create";
        const response = await runWith2FA({ method: isEdit ? "put" : "post", url, data: payload });
        toast.success(isEdit ? "Nouvelle version créée" : "Document créé");
        onSuccess(response.data);
    } catch (error) {
        if (error?.message === "2fa_cancelled") return;
        const msg = error.response?.data?.message || error.response?.data;
        toast.error(typeof msg === "string" ? msg : "Erreur lors de l'enregistrement");
    } finally {
        setLoading(false);
    }
}

/**
 * Renders the old content preview panel in a sticky scrollable container.
 * Only shown in edit mode when previous sections are available.
 * @param {object} props
 * @param {Array} props.previewSections - Sections from the selected old version
 * @param {Array} props.versions - All available versions
 * @param {string} props.selectedId - Currently selected version ID
 * @param {function} props.setSelectedId - Version selection change handler
 * @returns {React.ReactElement}
 */
function OldContentPanel({ previewSections, versions, selectedId, setSelectedId }) {
    return (
        <Box position="sticky" top="0" p="16px" borderRadius="12px" bg="whiteAlpha.100"
            maxH="calc(100vh - 100px)" overflowY="auto">
            <OldContentPreview sections={previewSections} versions={versions}
                selectedId={selectedId} onVersionChange={setSelectedId} />
        </Box>
    );
}

/**
 * Renders the preview toggle button row for the form fields column.
 * @param {object} props
 * @param {boolean} props.preview - Whether the user preview is active
 * @param {function} props.setPreview - Setter for the preview flag
 * @returns {React.ReactElement}
 */
function PreviewToggleRow({ preview, setPreview }) {
    return (
        <Flex justify="flex-end">
            <Button variant="ghost" leftIcon={<Icon as={MdVisibility} />}
                color={preview ? "brand.500" : "secondaryGray.500"} onClick={() => setPreview(!preview)}>
                {preview ? "Revenir à l'édition" : "Aperçu utilisateur"}
            </Button>
        </Flex>
    );
}

/**
 * Renders the form fields column with all editable document properties.
 * @param {object} props
 * @param {object} props.form - Current form state
 * @param {boolean} props.isEdit - Whether editing an existing document
 * @param {object|null} props.doc - Document being edited
 * @param {Array} props.types - Available document types
 * @param {function} props.onChange - Field change handler
 * @returns {React.ReactElement}
 */
function FormFieldsColumn({ form, isEdit, doc, types, hasOldContent, onChange }) {
    const [preview, setPreview] = useState(false);
    return (
        <Stack spacing="16px" maxW="900px" mx={hasOldContent ? "0" : "auto"}>
            {isEdit && <EditWarningBanner version={doc.version} />}
            <TypeVersionFields form={form} isEdit={isEdit} types={types} onChange={onChange} />
            <LanguageRoleFields form={form} onChange={onChange} />
            <PlatformDateFields form={form} onChange={onChange} />
            {isEdit && <ChangeTypeFields form={form} onChange={onChange} />}
            <Divider borderColor="whiteAlpha.200" />
            <PreviewToggleRow preview={preview} setPreview={setPreview} />
            {preview
                ? <DetailContentSections sections={form.content?.sections || []} />
                : <ContentEditor content={form.content} onChange={(c) => onChange("content", c)}
                    originalSections={isEdit ? (doc?.content?.sections || null) : null} />}
        </Stack>
    );
}

/**
 * Renders the drawer body grid with optional old-content comparison column.
 * @param {object} props - Layout props forwarded from FormDrawerLayout
 * @returns {React.ReactElement}
 */
function FormBodyGrid({ hasOldContent, previewSections, versions, selectedId, setSelectedId, form, isEdit, doc, types, onChange }) {
    return (
        <Grid templateColumns={{ base: "1fr", md: "repeat(12, 1fr)" }} gap="24px">
            {hasOldContent && (
                <GridItem colSpan={{ base: 12, md: 5 }}>
                    <OldContentPanel previewSections={previewSections} versions={versions}
                        selectedId={selectedId} setSelectedId={setSelectedId} />
                </GridItem>
            )}
            <GridItem colSpan={{ base: 12, md: hasOldContent ? 7 : 12 }}>
                <FormFieldsColumn form={form} isEdit={isEdit} doc={doc} types={types}
                    hasOldContent={hasOldContent} onChange={onChange} />
            </GridItem>
        </Grid>
    );
}

/**
 * Renders the drawer layout with header, form body, and confirmation dialog.
 * @param {object} props - All layout props forwarded from LegalDocumentForm
 * @returns {React.ReactElement}
 */
function FormDrawerLayout(props) {
    const { open, onClose, isEdit, doc, loading, onSubmit,
        hasOldContent, previewSections, versions, selectedId, setSelectedId,
        form, types, onChange, confirmOpen, setConfirmOpen, onSuccess, setLoading, runWith2FA } = props;

    return (
        <>
            <Drawer isOpen={open} placement="bottom" onClose={onClose} size="full">
                <DrawerOverlay />
                <DrawerContent>
                    <FormHeader isEdit={isEdit} doc={doc} loading={loading} onClose={onClose} onSubmit={onSubmit} />
                    <DrawerBody px="24px" py="16px">
                        <FormBodyGrid hasOldContent={hasOldContent} previewSections={previewSections}
                            versions={versions} selectedId={selectedId} setSelectedId={setSelectedId}
                            form={form} isEdit={isEdit} doc={doc} types={types} onChange={onChange} />
                    </DrawerBody>
                </DrawerContent>
            </Drawer>
            <ConfirmActivationDialog open={confirmOpen} onCancel={() => setConfirmOpen(false)}
                onConfirm={() => { setConfirmOpen(false); submitFormToApi(form, isEdit, doc, setLoading, onSuccess, runWith2FA); }} />
        </>
    );
}

/**
 * Full-screen Drawer form for creating or editing a legal document.
 * Slides up from the bottom and shows old content comparison when editing.
 * @param {object} props
 * @param {boolean} props.open - Whether the drawer is open
 * @param {object|null} props.document - Document to edit, or null for creation
 * @param {Array} props.types - Available document types from API
 * @param {function} props.onClose - Callback to close the drawer
 * @param {function} props.onSuccess - Callback with saved document on success
 * @returns {React.ReactElement}
 */
function LegalDocumentForm({ open, document: doc, types, onClose, onSuccess }) {
    const [form, setForm] = useState(buildInitialState(doc));
    const [loading, setLoading] = useState(false);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const isEdit = Boolean(doc);
    const runWith2FA = useWith2FA();
    const { versions, selectedId, setSelectedId, previewSections } = useVersionHistory(doc, isEdit);
    const hasOldContent = isEdit && previewSections?.length > 0;

    useEffect(() => { setForm(buildInitialState(doc)); }, [doc]);

    /** @param {string} field @param {*} value */
    const handleFieldChange = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    /** Validates then submits or shows confirmation dialog. */
    const handleSubmit = () => {
        if (!form.type) { toast.error("Veuillez sélectionner un type de document"); return; }
        if (!isEdit && !form.version) { toast.error("Veuillez saisir une version"); return; }
        if (isEdit && !form.effective_date) { setConfirmOpen(true); return; }
        submitFormToApi(form, isEdit, doc, setLoading, onSuccess, runWith2FA);
    };

    return (
        <FormDrawerLayout open={open} onClose={onClose} isEdit={isEdit} doc={doc}
            loading={loading} onSubmit={handleSubmit}
            hasOldContent={hasOldContent} previewSections={previewSections} versions={versions}
            selectedId={selectedId} setSelectedId={setSelectedId} form={form} types={types}
            onChange={handleFieldChange} confirmOpen={confirmOpen} setConfirmOpen={setConfirmOpen}
            onSuccess={onSuccess} setLoading={setLoading} runWith2FA={runWith2FA} />
    );
}

export default LegalDocumentForm;
